import { debounce, noop } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { EVISORT_AI } from '~/common/constants/users';
import { trackSegment } from '~/components/SegmentAnalytics';
import { useAgent } from '~/contexts/AgentContext';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { IconType } from '~/eds/src/types/IconType';
import { Nullable as EdsNullable } from '~/eds/src/types/Nullable';
import { SharedPanelProps } from '~/eds/src/types/SharedPanelProps';
import { StatusType } from '~/eds/src/types/StatusType';
import { UserAction } from '~/eds/src/types/UserAction';
import { copy } from '~/eds/src/utils/copy';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { UploadStatusType } from '~/enums/UploadStatusType';
import { PROMPT_WORD_LIMIT } from '~/features/x-ray/constants';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import {
  mapUserRatingFromFeedbackDetails,
  MessageStreamEvent,
  parseServerMessage,
} from '~/redux/api/transformers/conversationalAi';
import { coerceRtkqError } from '~/redux/api/utils';
import { slices } from '~/redux/slices';
import { Entity, EntityType } from '~/types/Entity';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { useResolveChatBotEntity } from '../ask-anything-button/useResolveChatBotEntity';
import { AGENT_IDS } from '../chat-bot-panel/agentIds';
import { useWorkflowRoutingEnabled } from '../chat-bot-panel/hooks/useWorkflowRoutingEnabled';
import { Chat } from '../chat/Chat';
import {
  SSE_STREAM_TIMEOUT,
  THINKING_EVENTS_ALL_DONE,
  THINKING_EVENTS_FADE_TIME,
  THINKING_EVENTS_GENERATING_RESPONSE,
} from '../chatbot/constants';
import { useConversationPipeline } from '../hooks/useConversationPipeline';
import {
  FeedbackDetails,
  MessageAction,
  Message as MessageType,
} from '../types/chat';
import type { EntityContext, EntityContextItem } from '../types/chatbot';
import { Footer } from './Footer';
import { useGetMessageActions } from './hooks/hooks';
import { useDocumentStatusChecker } from './hooks/useDocumentStatusChecker';
import { useSse } from './hooks/useSse';
import { NewConversation } from './NewConversation';
import {
  getCitationWarningMessage,
  getStaticContextActions,
  getStreamEndpoint,
  is424Error,
  parseStreamEndMessage,
  replyAiMessage,
  replyUserMessage,
  SSE_REQUEST_PROPS,
} from './utils';

export interface Props {
  entity: Entity<EntityType>;
  context?: EntityContext;
  selectedContext?: Nullable<EntityContextItem>;
  hasAskAnything?: boolean;
  disableInput?: boolean;
  statusMessage?: {
    message: string;
    status?: 'danger' | 'warning' | 'info' | 'success' | 'active';
    title?: string;
    autoDismiss?: boolean;
  };
  onDisclaimerChange?: (hasAccepted: boolean) => void;
  onSelectContextFilter?: (context: Nullable<EntityContextItem>) => void;
  onSelectMessageSource: (message: MessageType, sourceIndex: number) => void;
  shouldDisableSources?: (message: MessageType) => boolean;
  panelProps?: SharedPanelProps;
  userMessageActions?: ({
    resolveIcon?: (message: MessageType) => IconType;
  } & MessageAction)[];
  onRegisterClearAction?: (
    action: {
      onClick: () => void;
      isLoading: boolean;
    } | null,
  ) => void;
}

export const ChatBot = ({
  entity,
  context,
  selectedContext,
  hasAskAnything = false,
  panelProps,
  disableInput,
  statusMessage: statusMessageProps,
  userMessageActions,
  onDisclaimerChange,
  onSelectContextFilter,
  onSelectMessageSource,
  shouldDisableSources,
}: Props) => {
  const hasConversationalAIV2 = useFlag(FlagType.ConversationalAIV2);
  const hasAskAiReportButton = useFlag(FlagType.AskAiReportButton);
  const hasNewConversationButtonInASKAIHeader = useFlag(
    FlagType.NewConversationButtonInASKAIHeader,
  );
  const intl = useIntl();
  const { createSseConnection } = useSse(intl);
  const { isActive, toast } = useToast();
  const { RETRY_ERROR } = useErrors();
  let toastId: string | number = '';
  let toastIndex = 0;
  const [messages, setMessages] = useState<MessageType[]>([]);

  const isWorkflowRoutingEnabled = useWorkflowRoutingEnabled();

  const agentContext = useAgent();
  const isWorkflowRoutingSelected =
    agentContext?.selectedAgent === AGENT_IDS.WORKFLOW_ROUTING;

  const shouldUseWorkflowContext =
    isWorkflowRoutingEnabled && isWorkflowRoutingSelected;

  const staticContextActions = getStaticContextActions(
    intl,
    isWorkflowRoutingEnabled && isWorkflowRoutingSelected,
    isWorkflowRoutingEnabled,
  );
  const [streamMessage, setStreamMessage] =
    useState<EdsNullable<MessageType>>(null);
  const [isLoadingReply, setIsLoadingReply] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingEvents, setThinkingEvents] = useState<string[]>([]);
  const chatbotData = useResolveChatBotEntity();

  const isEntityDocumentVersion = entity.type === 'document_version';

  const messageActions = useGetMessageActions();

  // Since this component manages fetching conversation data, it should also dispatch the current conversation ID to the store.
  const dispatch = useDispatch();
  const currentConversationId = useSelector(
    slices.chatbot.selectors.selectCurrentConversationId,
  );

  const {
    data: conversations,
    isLoading: isLoadingConversations,
    isError: isErrorConversations,
    error: errorConversations,
    isSuccess: isSuccessGetConversations,
    refetch: refetchGetConversations,
  } = api.endpoints.getConversations.useQuery(entity);

  const [
    createConversation,
    {
      isLoading: isLoadingCreateConversation,
      isError: isErrorCreateConversation,
      error: errorCreateConversation,
    },
  ] = api.endpoints.createConversation.useMutation();
  const [
    getConversation,
    {
      isLoading: isLoadingGetConversation,
      isFetching: isFetchingGetConversation,
      isError: isErrorGetConversation,
      isUninitialized: isUninitializedGetConversation,
      error: errorGetConversation,
    },
  ] = api.endpoints.getConversation.useLazyQuery();

  const [
    postUserMessage,
    {
      isLoading: isLoadingSendMessage,
      error: errorPostUserMessage,
      reset: resetPostUserMessage,
    },
  ] = api.endpoints.postUserMessage.useMutation();

  const [interruptAiMessage, { isLoading: isInterruptingAiMessage }] =
    api.endpoints.interruptAiMessage.useMutation();

  const [postUserFeedback] = api.endpoints.postUserFeedback.useMutation();

  const hasGetConversation424Error = is424Error(errorGetConversation);
  const hasPostMessage424Error = is424Error(errorPostUserMessage);

  const onFinishDocumentProcessing = () => {
    if (
      errorCreateConversation &&
      (errorCreateConversation as Error).cause === HttpStatusCodeType.NotFound
    ) {
      handleNewConversation();
    }
  };
  const {
    statusMessage,
    status: documentProcessingStatus,
    isProcessing: isDocumentProcessing,
  } = useDocumentStatusChecker({
    entity,
    onFinishProcessing: onFinishDocumentProcessing,
  });
  const isDocumentProcessingError =
    documentProcessingStatus === UploadStatusType.OcrFailure;

  const hasAcceptedLegalDisclaimer =
    coerceRtkqError(errorConversations)?.request?.status !==
    HttpStatusCodeType.UnavailableForLegalReasons;

  const isLoadingRenderData =
    isSuccessGetConversations &&
    isUninitializedGetConversation &&
    !isErrorCreateConversation;

  const isLoadingConversation =
    isLoadingConversations ||
    isLoadingCreateConversation ||
    isLoadingGetConversation ||
    isLoadingRenderData;

  const isErrorConversation =
    (isErrorConversations && hasAcceptedLegalDisclaimer) ||
    isErrorCreateConversation ||
    isErrorGetConversation;
  const isPosting = isStreaming || isLoadingSendMessage;

  const [acceptLegalDisclaimer] =
    api.endpoints.acceptLegalDisclaimer.useLazyQuery();
  const hasAutoAccepted = useRef(false);
  const [isAutoAccepting, setIsAutoAccepting] = useState(
    !hasAcceptedLegalDisclaimer,
  );

  useEffect(() => {
    if (!hasAcceptedLegalDisclaimer && !hasAutoAccepted.current) {
      hasAutoAccepted.current = true;
      setIsAutoAccepting(true);
      acceptLegalDisclaimer({})
        .unwrap()
        .then(() => {
          refetchGetConversations();
        })
        .catch(() => {
          hasAutoAccepted.current = false;
        })
        .finally(() => {
          setIsAutoAccepting(false);
        });
    } else if (hasAcceptedLegalDisclaimer) {
      setIsAutoAccepting(false);
    }
  }, [
    hasAcceptedLegalDisclaimer,
    acceptLegalDisclaimer,
    refetchGetConversations,
  ]);

  useEffect(() => {
    if (isDocumentProcessingError && selectedContext?.type === 'document') {
      dispatch(slices.chatbot.actions.setSelectedContext(null));
    }
  }, [isDocumentProcessingError]);

  useEffect(() => {
    onDisclaimerChange?.(true);
  }, [onDisclaimerChange]);

  useEffect(() => {
    if (conversations && currentConversationId === null) {
      if (conversations.items.length > 0) {
        const currentConversationId = conversations.items[0].id;
        dispatch(
          slices.chatbot.actions.setCurrentConversationId(
            currentConversationId,
          ),
        );
      } else {
        handleNewConversation();
      }
    }
  }, [conversations]);

  useEffect(() => {
    if (currentConversationId) {
      getConversation({
        conversationId: currentConversationId,
      })
        .unwrap()
        .then((conversation) => {
          if (conversation) {
            setMessages(conversation.messages as MessageType[]);
          }
        })
        .catch((error) => {
          if (is424Error(error)) {
            showToast(
              intl.formatMessage({
                id: 'ChatBot.conversationRetrievalError',
                defaultMessage:
                  'There was an error retrieving this conversation. Please start a new conversation.',
                description: 'Error message when conversation retrieval fails',
              }),
            );
          }
        });
    }
    return () => {
      if (currentConversationId) {
        interruptAiMessage(currentConversationId);
      }
    };
  }, [currentConversationId]);

  const resetStream = () => {
    setStreamMessage(null);
    setIsStreaming(false);
    onFinishMessage();
  };

  const handleNewConversation = useCallback(() => {
    interruptAiMessage(currentConversationId ?? '')
      .unwrap()
      .then(() => {
        if (!streamMessage?.text) {
          setStreamMessage(null);
        }
      })
      .finally(() => {
        resetPostUserMessage();
        createConversation(chatbotData.entity)
          .unwrap()
          .then((newConversation) => {
            if (
              newConversation &&
              newConversation.id !== currentConversationId
            ) {
              dispatch(
                slices.chatbot.actions.setCurrentConversationId(
                  newConversation.id,
                ),
              );
              const newSelectedContext = context?.length
                ? context[context.length - 1]
                : null;
              dispatch(
                slices.chatbot.actions.setSelectedContext(newSelectedContext),
              );
              dispatch(
                slices.chatbot.actions.overrideEntity(chatbotData.entity),
              );

              if (!hasAskAnything) {
                setStreamMessage(replyAiMessage());
                setIsStreaming(true);
                handleNewSseConnection(newConversation.id);
              }
            }
          });
      });
  }, [
    currentConversationId,
    streamMessage,
    chatbotData.entity,
    context,
    hasAskAnything,
    interruptAiMessage,
    resetPostUserMessage,
    createConversation,
    dispatch,
  ]);

  const handleNewConversationClick = useCallback(() => {
    setMessages([]);
    handleNewConversation();
    trackSegment('selectClearChatHistory', {
      conversationId: currentConversationId,
    });
  }, [handleNewConversation, currentConversationId]);

  const newConversationTooltip = intl.formatMessage({
    id: 'ChatBot.newConversationTooltip',
    defaultMessage: 'New Conversation',
    description: 'Tooltip for new conversation button',
  });

  const conversationActions: UserAction[] = useMemo(() => {
    if (!hasAskAnything) return [];

    return [
      {
        text: '',
        icon: 'new-conversation' as const,
        tooltip: newConversationTooltip,
        onClick: handleNewConversationClick,
        isLoading: isLoadingConversation,
      },
    ];
  }, [
    hasAskAnything,
    isLoadingConversation,
    newConversationTooltip,
    handleNewConversationClick,
  ]);

  const chatActions: UserAction[] = useMemo(() => {
    if (hasNewConversationButtonInASKAIHeader) return [];

    if (!hasAskAnything) return [];

    return [
      {
        icon: 'plus',
        mode: 'icon',
        text: '',
        tooltip: newConversationTooltip,
        disabled: isLoadingConversation,
        onClick: () => {
          setMessages([]);
          handleNewConversation();
          trackSegment('selectClearChatHistory', {
            conversationId: currentConversationId,
          });
        },
      },
    ];
  }, [
    hasNewConversationButtonInASKAIHeader,
    hasAskAnything,
    isLoadingConversation,
    newConversationTooltip,
    handleNewConversation,
    currentConversationId,
  ]);

  const handleNewSseConnection = (id: Uuid) =>
    createSseConnection(
      getStreamEndpoint(id),
      {
        onAuthenticationError: resetStream,
        onClose: noop,
        onError: resetStream,
        onMessage: handleStreamMessage,
        onOpen: handleStreamOpen,
        onRateLimitError: resetStream,
        onTimeout: resetStream,
      },
      {
        ...SSE_REQUEST_PROPS,
        timeout: SSE_STREAM_TIMEOUT,
      },
    );

  const handleStreamOpen = useCallback(() => {
    setThinkingEvents([]);
    setStreamMessage(replyAiMessage());
  }, []);

  const clearStreamMessage = debounce(
    () => setStreamMessage(null),
    THINKING_EVENTS_FADE_TIME,
  );
  const debouncedSetMessages = useCallback(
    debounce((newMessage: MessageType) => {
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    }, THINKING_EVENTS_FADE_TIME),
    [],
  );

  const runAfterAnimation = (fn: Function) => {
    setTimeout(() => fn(), THINKING_EVENTS_FADE_TIME);
  };

  const handleStreamMessage = (data: unknown) => {
    const messageEvent = data as MessageStreamEvent;
    switch (messageEvent.eventType) {
      case 'thinking':
        setThinkingEvents((prevEvents) => [
          ...prevEvents,
          messageEvent.text ?? '',
        ]);

        if (messageEvent.text === THINKING_EVENTS_GENERATING_RESPONSE) {
          setIsLoadingReply(true);
        }
        break;
      case 'message_start': {
        setStreamMessage((prevMessage) => {
          if (prevMessage) {
            return { ...prevMessage, text: messageEvent.text ?? '' };
          }
          return prevMessage;
        });
        break;
      }
      case 'message_delta': {
        setIsLoadingReply(false);
        if (messageEvent?.text) {
          setStreamMessage((prevMessage) => {
            if (prevMessage) {
              const text = prevMessage.text + messageEvent.text;
              return {
                ...prevMessage,
                text: text,
              };
            }
            return prevMessage;
          });
        }
        break;
      }
      case 'message_response_suggestion': {
        setStreamMessage((prevMessage) => {
          if (prevMessage && messageEvent?.text) {
            const updatedSuggestions = prevMessage.choices
              ? [...prevMessage.choices, messageEvent.text]
              : [messageEvent.text];
            return { ...prevMessage, choices: updatedSuggestions };
          }
          return prevMessage;
        });
        break;
      }
      case 'message_end': {
        if (messageEvent.message) {
          const [citationWarning]: Array<string> =
            (
              messageEvent?.message?.additionalInfo as {
                citationsWarnings?: string[];
              }
            )?.citationsWarnings ?? [];

          if (citationWarning) {
            showToast(getCitationWarningMessage(citationWarning), 'warning');
          }

          const newMessage = parseServerMessage(messageEvent.message);
          debouncedSetMessages(newMessage);
        }
        setThinkingEvents((prevEvents) => [
          ...prevEvents,
          THINKING_EVENTS_ALL_DONE,
        ]);
        clearStreamMessage();

        break;
      }
      case 'stream_end': {
        setIsStreaming(false);
        runAfterAnimation(onFinishMessage);
        if (messageEvent.text) {
          const serverMessage = parseStreamEndMessage(messageEvent.text);
          showToast(serverMessage);
        }
        clearStreamMessage();
        break;
      }

      default:
        return;
    }
  };
  const handleSubmitFeedback = useCallback(
    (message: MessageType, feedbackDetails: FeedbackDetails) => {
      if (!currentConversationId) return;

      postUserFeedback({
        conversationId: currentConversationId,
        messageId: message.id,
        feedbackDetails,
      })
        .unwrap()
        .then(() => {
          trackSegment('selectChatMessageFeedback', {
            messageId: message.id,
            userRating: mapUserRatingFromFeedbackDetails(feedbackDetails),
            userComment: feedbackDetails.comment,
          });
          if (feedbackDetails.comment) {
            toast({
              message: intl.formatMessage({
                id: 'ChatBot.feedbackSubmitted',
                defaultMessage: 'Thank you, your feedback has been submitted.',
                description: 'Thank you message when feedback is submitted',
              }),
              status: 'success',
            });
          }
        })
        .catch((error) => showToast(error?.detail ?? RETRY_ERROR));
    },
    [currentConversationId],
  );

  const handleChoose = useCallback((message: MessageType, choice: string) => {
    trackSegment('selectChatMessageChoice', {
      messageId: message.id,
      choice,
    });
  }, []);
  const handlePost = useCallback(
    (
      updatedText: string,
      metadata?: { questionId: Uuid; questionGroupId: Uuid },
    ) => {
      if (hasAskAnything && !selectedContext) {
        showToast(
          intl.formatMessage({
            id: 'ChatBot.selectContextMessage',
            defaultMessage: 'Please select a context to continue.',
            description:
              'Message shown when user tries to post without selecting a context',
          }),
        );
        return;
      }

      if (!currentConversationId) return;

      setMessages((prevMessages) => [
        ...prevMessages,
        replyUserMessage(updatedText),
      ]);

      postUserMessage({
        conversationId: currentConversationId,
        message: updatedText,
        metadata: metadata,
        context: selectedContext
          ? [
              {
                search_entity: selectedContext.searchEntity,
                search_query: selectedContext.query,
                selected_ids: selectedContext.selectedIds,
                entities_in_view: selectedContext.searchEntityContext,
              },
            ]
          : [],
      })
        .unwrap()
        .then((message) => {
          if (message) {
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              const userMessage = updatedMessages.pop();
              updatedMessages.push({
                ...userMessage,
                ...(message as MessageType),
              });
              return updatedMessages;
            });
            setIsStreaming(true);
            handleNewSseConnection(currentConversationId);
            trackSegment('enterChatQuestion', {
              conversationId: currentConversationId,
            });
          }
        })
        .catch((error) => {
          onFinishMessage();
          if (is424Error(error)) {
            showToast(
              intl.formatMessage({
                id: 'ChatBot.messagePostingError',
                defaultMessage:
                  'There was an error posting a new message to this conversation. Please start a new conversation.',
                description: 'Error message when posting a message fails',
              }),
            );
          } else {
            showToast(error?.detail ?? RETRY_ERROR);
          }
          setStreamMessage(null);
          setIsStreaming(false);
          setIsLoadingReply(false);
        });
    },
    [currentConversationId, selectedContext, hasAskAnything],
  );

  // this will allow other components to call the handlePost function
  // returning noop if no context is selected
  const { onFinishMessage } = useConversationPipeline({
    processHandler: selectedContext ? handlePost : noop,
  });

  const getInterruptAction = (conversationId: Nullable<string>) => ({
    icon: 'circle-stop' as const,
    isLoading: isInterruptingAiMessage,
    onClick: () => {
      if (conversationId) {
        interruptAiMessage(conversationId)
          .unwrap()
          .then(() => {
            if (!streamMessage?.text) {
              setStreamMessage(null);
            }
          });
        trackSegment('chatStopGenerating', {
          conversationId,
        });
      }
    },
    text: intl.formatMessage({
      id: 'ChatBot.stopGenerating',
      defaultMessage: 'Stop Generating',
      description: 'Button text to stop AI message generation',
    }),
  });

  const showToast = (message: string, status: StatusType = 'danger') => {
    if (!isActive(toastId)) {
      toastId = toast({
        message,
        status,
        key: `chatbot-error-${toastIndex}`,
        onDismiss: () => toastIndex++,
      });
    }
  };

  useEffect(() => {
    if (isLoadingSendMessage) {
      setIsLoadingReply(true);
      setIsStreaming(true);
      setStreamMessage(replyAiMessage());
      setThinkingEvents([]);
    }
  }, [isLoadingSendMessage]);

  const loadingContent = {
    isLoading: isLoadingConversation || isAutoAccepting,
    message: intl.formatMessage({
      id: 'ChatBot.loadingMessage',
      defaultMessage: 'Loading chat…',
      description: 'Loading message displayed while chat is being loaded',
    }),
  };

  const placeholderContent =
    isErrorCreateConversation &&
    conversations &&
    conversations.items.length === 0
      ? {
          icon: 'chatbot' as const,
          message: intl.formatMessage({
            id: 'ChatBot.unableToCreateConversation',
            defaultMessage:
              'Unable to create a conversation. If the document is still processing, Ask AI will automatically refresh when it is ready.',
            description: 'Error message when conversation creation fails',
          }),
        }
      : isErrorConversation
        ? {
            icon: 'chatbot' as const,
            message: intl.formatMessage({
              id: 'ChatBot.unableToLoadHistory',
              defaultMessage:
                'Unable to load chat history. Refresh the page to try again.',
              description: 'Error message when chat history fails to load',
            }),
          }
        : undefined;

  const moreActions = hasAcceptedLegalDisclaimer
    ? [
        {
          text: intl.formatMessage({
            id: 'ChatBot.clearChatHistory',
            defaultMessage: 'Clear your chat history',
            description: 'Button text to clear chat history',
          }),
          tooltip: isStreaming
            ? intl.formatMessage({
                id: 'ChatBot.disabledWhileGenerating',
                defaultMessage: 'Disabled while generating.',
                description:
                  'Tooltip shown when action is disabled during message generation',
              })
            : undefined,
          disabled: isLoadingConversation,
          onClick: () => {
            setMessages([]);
            handleNewConversation();
            trackSegment('selectClearChatHistory', {
              conversationId: currentConversationId,
            });
          },
        },
        ...(hasAskAiReportButton && currentConversationId
          ? [
              {
                text: intl.formatMessage({
                  id: 'ChatBot.copyConversationId',
                  defaultMessage: 'Copy Conversation ID',
                  description:
                    'Button text to copy conversation ID to clipboard',
                }),
                onClick: () => {
                  copy(currentConversationId);
                  toast({
                    message: intl.formatMessage({
                      id: 'ChatBot.conversationIdCopied',
                      defaultMessage: 'Conversation ID copied to clipboard',
                      description:
                        'Toast message shown when conversation ID is copied',
                    }),
                    status: 'success',
                  });
                },
              },
            ]
          : []),
      ]
    : [];
  const contextFilters: UserAction[] = useMemo(
    () =>
      (context ?? [])
        .filter((contextItem) => {
          if (
            shouldUseWorkflowContext &&
            contextItem.type === 'all_documents'
          ) {
            return false;
          }
          return true;
        })
        .map((contextItem) => ({
          text: contextItem.label,
          onClick: () => {
            if (selectedContext?.id !== contextItem.id) {
              onSelectContextFilter?.(contextItem);
            }
          },
          mode: 'chip' as const,
          level:
            selectedContext?.id === contextItem.id
              ? 'secondary-active'
              : undefined,
          ...(contextItem.type === 'document'
            ? {
                disabled: isDocumentProcessingError || isDocumentProcessing,
                tooltip: isDocumentProcessingError
                  ? intl.formatMessage({
                      id: 'ChatBot.documentProcessingError',
                      defaultMessage:
                        'Evisort AI was not able to process this document.',
                      description: 'Tooltip when document processing failed',
                    })
                  : isDocumentProcessing
                    ? intl.formatMessage({
                        id: 'ChatBot.documentProcessingInProgress',
                        defaultMessage:
                          'Evisort AI is processing this document. It will be available in a few minutes.',
                        description:
                          'Tooltip when document is still being processed',
                      })
                    : undefined,
              }
            : {}),
        })),
    [
      context,
      selectedContext,
      isEntityDocumentVersion,
      isDocumentProcessingError,
      isDocumentProcessing,
      intl,
      shouldUseWorkflowContext,
    ],
  );

  const footer = useMemo(() => <Footer entity={entity} />, []);

  const getActiveContext = () => {
    if (!hasAskAnything) return undefined;

    if (shouldUseWorkflowContext) {
      if (selectedContext?.type === 'workflow') {
        return {
          icon: 'filter' as const,
          text: intl.formatMessage({
            id: 'ChatBotUtils.workflow',
            defaultMessage: 'Workflow',
            description: 'Option to search within workflow',
          }),
          onReset: () => onSelectContextFilter?.(null),
          disableReset: false,
        };
      }
      if (selectedContext?.type === 'all_documents') {
        return {
          icon: selectedContext.icon,
          text: selectedContext.title,
          onReset: undefined,
        };
      }
      if (selectedContext?.type === 'document') {
        return {
          icon: selectedContext.icon,
          text: selectedContext.title,
          onReset: () => onSelectContextFilter?.(null),
          disableReset: false,
        };
      }

      return {
        icon: 'filter' as const,
        text: intl.formatMessage({
          id: 'ChatBot.documentsFilter',
          defaultMessage: 'Documents',
          description: 'Default filter label for documents',
        }),
        onReset: undefined,
      };
    }

    if (selectedContext) {
      return {
        icon: selectedContext.icon,
        text: selectedContext.title,
        onReset: () => onSelectContextFilter?.(null),
        // Disable reset if the entity is a document version interim solution while we fully support scope switch
        disableReset: !hasConversationalAIV2
          ? entity.type === 'document_version'
          : false,
      };
    } else {
      return {
        icon: 'filter' as const,
        text: intl.formatMessage({
          id: 'ChatBot.documentsFilter',
          defaultMessage: 'Documents',
          description: 'Default filter label for documents',
        }),
        onReset: undefined,
      };
    }
  };

  const conspicuousFooter = (
    <Layout justify="center">
      <Text variant="tiny" color="text.quiet" fontWeight="500">
        {intl.formatMessage({
          id: 'ChatBot.conspicuousFooter',
          defaultMessage:
            'AI may occasionally be incorrect. Please verify important details before taking action.',
          description: 'Conspicuous footer disclaimer for Evisort AI',
        })}
      </Text>
    </Layout>
  );

  return (
    <Chat
      disclaimer={{ hasAccepted: true }}
      footer={!hasAskAnything ? footer : conspicuousFooter}
      statusMessage={statusMessage ?? statusMessageProps}
      context={
        hasAskAnything && !shouldUseWorkflowContext
          ? {
              label: intl.formatMessage({
                id: 'ChatBot.searchLabel',
                defaultMessage: 'Search',
                description: 'Label for search context',
              }),
              actions: contextFilters,
            }
          : undefined
      }
      disableInput={
        disableInput ||
        hasGetConversation424Error ||
        hasPostMessage424Error ||
        isFetchingGetConversation
      }
      activeContext={getActiveContext()}
      activeContextActions={staticContextActions}
      interruptAction={getInterruptAction(currentConversationId)}
      emptyConversation={hasAskAnything ? <NewConversation /> : undefined}
      isPosting={isPosting}
      isPrivate={true}
      limit={PROMPT_WORD_LIMIT}
      loadingContent={loadingContent}
      isLoadingReply={isLoadingReply}
      messages={messages}
      moreActions={moreActions}
      otherUser={EVISORT_AI}
      placeholderContent={placeholderContent}
      replyMessage={streamMessage}
      shouldDisableSources={shouldDisableSources}
      title={intl.formatMessage({
        id: 'ChatBot.title',
        defaultMessage: 'Conversational AI',
        description: 'Title of the chat interface',
      })}
      messageActions={messageActions}
      panelProps={
        hasNewConversationButtonInASKAIHeader
          ? {
              ...panelProps,
              actions: [...conversationActions, ...(panelProps?.actions ?? [])],
            }
          : panelProps
      }
      chatActions={chatActions}
      onChoose={handleChoose}
      onPost={handlePost}
      onSelectMessageSource={onSelectMessageSource}
      onSubmitFeedback={handleSubmitFeedback}
      thinkingEvents={thinkingEvents}
      userMessageActions={userMessageActions}
    />
  );
};
